import { useEffect, useState } from "react";
import type { DataTableColumn } from "mantine-datatable";
import BasePage from "../BasePage";
import { TableOverlay } from "../../components/LoadingOverlay";
import ResponsiveTable from "../../components/ResponsiveTable";
import { coreApi } from "../../utils/CoreAPI";
import AuditLog from "../../interfaces/AuditLog";
import { Stack, Table, Text } from "@mantine/core";
import dayjs from "dayjs";
import DrawerBox from "../../components/DrawerBox";

function AuditLogsPage() {
    const [loading, setLoading] = useState(true);
    const [logsData, setLogsData] = useState<AuditLog[]>([]);
    const [selectedLog, setSelectedLog] = useState<AuditLog>();
    const [open, setOpen] = useState<boolean>(false);

    const columns: DataTableColumn<AuditLog>[] = [
        { accessor: "timestamp", title: "Timestamp", sortable: true, render: (record) => dayjs(record.timestamp).format('DD/MM/YYYY, hh:mm A') },
        { accessor: "request_id", title: "Request ID", sortable: false },
        { accessor: "category", title: "Category", sortable: true },
        { accessor: "action", title: "Action", sortable: true },
    ];

    useEffect(() => {
        const getApiKeys = async () => {
            const response = await coreApi.get("/admin/audit-logs");

            setLogsData(response.data);
            setLoading(false);
        };

        getApiKeys();
    }, []);

    return (
        <BasePage
            breadcrumbs={[{ label: "Audit Logs" }]}
            fullWidth
        >
            <TableOverlay visible={loading}>
                <ResponsiveTable
                    data={logsData}
                    columns={columns}
                    gridRowId={(row) => row.id}
                    onRowClick={(row) => { setSelectedLog(row), setOpen(true) }}
                    selectionColumnStyle={{ display: 'none' }}
                />
            </TableOverlay>

            <DrawerBox
                isOpen={open}
                onClose={() => {setOpen(false)}}
                withTitle
                titleDivider
                withCloseButton
                title={selectedLog?.action}
                position="right"
            >
                <Stack>
                    <Text>Request Details</Text>

                    <Table variant="vertical" withTableBorder withColumnBorders>
                        <Table.Tbody>
                            <Table.Tr>
                                <Table.Th w={160}>Timestamp</Table.Th>
                                <Table.Td>{dayjs(selectedLog?.timestamp).format('DD/MM/YYYY, hh:mm A')}</Table.Td>
                            </Table.Tr>

                            <Table.Tr>
                                <Table.Th w={160}>Request ID</Table.Th>
                                <Table.Td>{selectedLog?.request_id}</Table.Td>
                            </Table.Tr>

                            <Table.Tr>
                                <Table.Th w={160}>IP Address</Table.Th>
                                <Table.Td>{selectedLog?.remote_ip}</Table.Td>
                            </Table.Tr>

                            <Table.Tr>
                                <Table.Th w={160}>User-Agent</Table.Th>
                                <Table.Td>{selectedLog?.user_agent}</Table.Td>
                            </Table.Tr>
                        </Table.Tbody>
                    </Table>

                    <Text>Endpoint Details</Text>

                    <Table variant="vertical" withTableBorder withColumnBorders>
                        <Table.Tbody>
                            <Table.Tr>
                                <Table.Th w={160}>Category</Table.Th>
                                <Table.Td>{selectedLog?.category}</Table.Td>
                            </Table.Tr>

                            <Table.Tr>
                                <Table.Th w={160}>Action</Table.Th>
                                <Table.Td>{selectedLog?.action}</Table.Td>
                            </Table.Tr>
                        </Table.Tbody>
                    </Table>

                    <Text>Initiator Details</Text>

                    <Table variant="vertical" withTableBorder withColumnBorders>
                        <Table.Tbody>
                            <Table.Tr>
                                <Table.Th w={160}>
                                    {selectedLog?.api_key_used ? "Key Prefix" : "User ID"}
                                </Table.Th>
                                <Table.Td>
                                    {selectedLog?.api_key_used ? selectedLog?.user_details['key_prefix'] : selectedLog?.user_details['oid']}
                                </Table.Td>
                            </Table.Tr>

                            <Table.Tr>
                                <Table.Th w={160}>
                                    {selectedLog?.api_key_used ? "Key Name" : "Display Name"}
                                </Table.Th>
                                <Table.Td>
                                    {selectedLog?.api_key_used ? selectedLog?.user_details['key_name'] : selectedLog?.user_details['name']}
                                </Table.Td>
                            </Table.Tr>

                            {!selectedLog?.api_key_used &&
                                <Table.Tr>
                                    <Table.Th w={160}>User Principal Name</Table.Th>
                                    <Table.Td>{selectedLog?.user_details['email']}</Table.Td>
                                </Table.Tr>
                            }
                        </Table.Tbody>
                    </Table>

                    <Text>Additional Details</Text>

                    <Table variant="vertical" withTableBorder withColumnBorders>
                        <Table.Tbody>
                            {
                                Object.entries(selectedLog?.meta ?? {}).map(([key, value]) => (
                                    <Table.Tr key={key}>
                                        <Table.Th w={160}>{String(key)}</Table.Th>
                                        <Table.Td>{String(value)}</Table.Td>
                                    </Table.Tr>
                                ))
                            }
                        </Table.Tbody>
                    </Table>
                </Stack>
            </DrawerBox>
        </BasePage>
    );
}

export default AuditLogsPage;